import { formatShortDate } from "@shared";
import { startOfDay } from "date-fns";
import { TFunction } from "i18next";

import { CourtDayResponse, CourtDayType, CourtResponse } from "@/api";
import { locales } from "@/common/contexts/localization-date-context/LocalizationDateContextProvider.utils";
import {
  ordinalShortLabels,
  weekdayShortLabels,
} from "@/pages/system-settings/tabs/courts/vienna/components/CourtLabels.utils";
import { LanguageType } from "@/translations";

const SINGLE_DATE_CHIP_THRESHOLD = 3;

/**
 * Composes a court's address into a single line, skipping any missing parts.
 * Only PLZ is guaranteed; e.g. "Marxergasse 1a, 1100 Wien, Österreich" or just "1100".
 */
export function buildCourtAddress(court: CourtResponse): string {
  const streetLine = [court.street, court.houseNumber].filter(Boolean).join(" ");
  const cityLine = [court.zipCode, court.city].filter(Boolean).join(" ");
  return [streetLine, cityLine, court.country].filter(Boolean).join(", ");
}

export interface CourtDayChip {
  label: string;
  kind: CourtDayType;
}

function weekdayChip(day: CourtDayResponse, t: TFunction): CourtDayChip {
  const weekday = day.weekday ? (weekdayShortLabels(t)[day.weekday] ?? "") : "";
  return { label: weekday, kind: CourtDayType.Weekday };
}

function ruleChip(day: CourtDayResponse, t: TFunction): CourtDayChip {
  const ordinal = day.ordinal ? (ordinalShortLabels(t)[day.ordinal] ?? "") : "";
  const weekday = day.weekday ? (weekdayShortLabels(t)[day.weekday] ?? "") : "";
  return { label: t("settings.courts.chip.rule", { ordinal, weekday }), kind: CourtDayType.Rule };
}

function singleDateChips(day: CourtDayResponse, t: TFunction, language: LanguageType): CourtDayChip[] {
  const today = startOfDay(new Date());
  const futureDates = day.dates.filter((date) => date >= today).sort((a, b) => a.getTime() - b.getTime());

  if (futureDates.length === 0) {
    return [];
  }

  if (futureDates.length > SINGLE_DATE_CHIP_THRESHOLD) {
    return futureDates.map((date) => ({
      label: t("settings.courts.chip.singleDate", { date: formatShortDate(date, locales[language]) }),
      kind: CourtDayType.SingleDates,
    }));
  }

  return [
    {
      label: t("settings.courts.chip.singleDatesSummary", {
        count: futureDates.length,
        dates: futureDates.map((date) => formatShortDate(date, locales[language])).join(", "),
      }),
      kind: CourtDayType.SingleDates,
    },
  ];
}

/** Builds the display chips for a court's Gerichtstage, showing only future entries. */
export function buildCourtDayChips(
  courtDays: CourtDayResponse[],
  t: TFunction,
  language: LanguageType
): CourtDayChip[] {
  return courtDays.flatMap((day) => {
    switch (day.type) {
      case CourtDayType.Weekday:
        return [weekdayChip(day, t)];
      case CourtDayType.Rule:
        return [ruleChip(day, t)];
      case CourtDayType.SingleDates:
        return singleDateChips(day, t, language);
      default:
        return [];
    }
  });
}
